import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    VStack,
    Text,
    HStack,
    Container,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Textarea,
    Flex,
    Center,
    Grid,
    Image,
    useColorModeValue,
    Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { AddIcon, CheckIcon, CloseIcon, ViewIcon } from '@chakra-ui/icons';
import { useAuth } from '../Utils/AuthContext';
import { useAppToast } from '../Components/Toast';
import { useCompany } from '../Utils/CompanyContext';
import { getAllCardsApi, updateCardStatusApi, normalizeCards, fetchPassportBlob } from '../Utils/ApiCall';
import { BusinessCard } from '../Utils/Storage';
import Navbar from '../Components/Navbar';
import { getInitials } from '../Utils/Helpers';

/** Tiny avatar that fetches passport blob for a given card. */
const PassportAvatar: React.FC<{ card: BusinessCard }> = ({ card }) => {
    const [src, setSrc] = React.useState<string | null>(null);
    React.useEffect(() => {
        let revoke: string | null = null;
        if (card.photo) {
            const lookupKey = card.photo.split('/').pop() || card.id;
            fetchPassportBlob(lookupKey).then(url => {
                if (url) { setSrc(url); revoke = url; }
            });
        }
        return () => { if (revoke) URL.revokeObjectURL(revoke); };
    }, [card.id, card.photo]);

    if (src) {
        return <Image src={src} alt={card.name} boxSize="full" objectFit="cover" />;
    }
    return (
        <Text fontSize="11px" fontWeight={700} color="brand.500">
            {getInitials(card.userName ?? card.name)}
        </Text>
    );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const s = status.toLowerCase();
    const isApproved = s === 'approved' || s === 'active';
    const isRejected = s === 'rejected' || s === 'reject';
    
    let bg = 'rgba(245, 158, 11, 0.15)';
    let color = '#F59E0B';
    let label = 'Pending';

    if (isApproved) {
        bg = 'rgba(34, 197, 94, 0.15)';
        color = '#22C55E';
        label = 'Approved';
    } else if (isRejected) {
        bg = 'rgba(239, 68, 68, 0.15)';
        color = '#EF4444';
        label = 'Rejected';
    }

    return (
        <HStack
            spacing={1.5}
            display="inline-flex"
            bg={bg}
            px={2.5}
            py={1}
            borderRadius="full"
            backdropFilter="blur(8px)"
            border="1px solid"
            borderColor="whiteAlpha.200"
        >
            <Box w="5px" h="5px" borderRadius="full" bg={color} flexShrink={0} />
            <Text fontSize="10.5px" fontWeight={700} color={color} textTransform="uppercase" letterSpacing="0.08em">
                {label}
            </Text>
        </HStack>
    );
};

const StatCard: React.FC<{ label: string; value: number | string; accentColor: string; bgGradient?: string }> = ({
    label, value, accentColor, bgGradient
}) => {
    const cardBg = useColorModeValue('#FFFFFF', '#0D1527');
    const cardBorder = useColorModeValue('rgba(13, 31, 76, 0.08)', 'rgba(255, 255, 255, 0.08)');
    const cardShadow = useColorModeValue('0 4px 20px rgba(13, 31, 76, 0.05)', '0 4px 20px rgba(0, 0, 0, 0.4)');
    const statLabelColor = useColorModeValue('gray.500', 'gray.400');

    return (
        <Box
            bg={cardBg}
            borderRadius="16px"
            border="1px solid"
            borderColor={cardBorder}
            boxShadow={cardShadow}
            px={6}
            py={5}
            position="relative"
            overflow="hidden"
            transition="all 0.2s"
            _hover={{ transform: 'translateY(-2px)' }}
        >
            <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                h="3px"
                bg={accentColor}
            />
            <Text
                fontSize="11px"
                fontWeight={700}
                color={statLabelColor}
                textTransform="uppercase"
                letterSpacing="0.09em"
                mb={2}
            >
                {label}
            </Text>
            <Text
                fontFamily="'Cormorant Garamond', Georgia, serif"
                fontSize="34px"
                fontWeight={500}
                color={accentColor}
                lineHeight={1}
            >
                {value}
            </Text>
        </Box>
    );
};

const SupervisorDashboard: React.FC = () => {
    const navigate = useNavigate();
    const toast = useAppToast();
    const { user } = useAuth();
    const { company } = useCompany();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [cards, setCards] = useState<BusinessCard[]>([]);
    const [selectedCard, setSelectedCard] = useState<BusinessCard | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [loading, setLoading] = useState(true);

    const pageBg = useColorModeValue('#F4F6FB', '#070C18');
    const tableBg = useColorModeValue('#FFFFFF', '#0D1527');
    const tableBorder = useColorModeValue('rgba(13, 31, 76, 0.08)', 'rgba(255, 255, 255, 0.08)');
    const tableHeaderBg = useColorModeValue('rgba(13, 31, 76, 0.02)', 'rgba(255, 255, 255, 0.02)');
    const trHoverBg = useColorModeValue('rgba(18, 100, 232, 0.04)', 'rgba(255, 255, 255, 0.03)');
    const headingColor = useColorModeValue('#0B132B', '#F8FAFC');
    const subtextColor = useColorModeValue('gray.600', 'gray.400');
    const tableBoxShadow = useColorModeValue('0 10px 30px rgba(13, 31, 76, 0.05)', '0 10px 30px rgba(0, 0, 0, 0.5)');
    const tableTitleColor = useColorModeValue('#0F172A', '#F8FAFC');
    const badgeBg = useColorModeValue('brand.50', 'whiteAlpha.150');
    const badgeColor = useColorModeValue('brand.600', 'accentCyan.300');
    const thColor = useColorModeValue('gray.500', 'gray.400');
    const tdAvatarBg = useColorModeValue('#EEF4FF', '#16233F');
    const tdAvatarBorder = useColorModeValue('white', 'whiteAlpha.200');
    const tdTextColor = useColorModeValue('#0F172A', '#F8FAFC');
    const tdSubtextColor = useColorModeValue('gray.500', 'gray.400');
    const tdDeptColor = useColorModeValue('gray.600', 'gray.300');
    const tdDateColor = useColorModeValue('gray.500', 'gray.400');
    const tdActionViewColor = useColorModeValue('gray.600', 'gray.300');
    const tdActionViewHoverBg = useColorModeValue('blackAlpha.050', 'whiteAlpha.150');
    const emptyTextColor = useColorModeValue('gray.500', 'gray.400');
    const modalBg = useColorModeValue('white', '#0D1527');
    const modalBorder = useColorModeValue('gray.200', 'whiteAlpha.150');
    const modalTitleColor = useColorModeValue('#0F172A', 'white');
    const modalSubtextColor = useColorModeValue('gray.600', 'gray.400');
    const textareaBg = useColorModeValue('white', 'whiteAlpha.050');
    const textareaColor = useColorModeValue('#0F172A', 'white');
    const textareaBorder = useColorModeValue('gray.300', 'whiteAlpha.200');
    const modalCancelBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
    const modalCancelColor = useColorModeValue('gray.600', 'gray.400');

    const loadCards = React.useCallback(async () => {
        try {
            const res = await getAllCardsApi();
            if (res.data.success) {
                const normalized = normalizeCards(res.data.data);
                setCards(normalized);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user?.role !== 'Supervisor' && user?.role !== 'Admin') {
            navigate('/dashboard');
            return;
        }
        loadCards();
    }, [user, navigate, loadCards]);

    const handleApprove = async (id: string) => {
        try {
            await updateCardStatusApi(id, 'active');
            toast({
                title: 'Card approved',
                description: 'The employee digital card is now live.',
                status: 'success',
                duration: 2500,
            });
            loadCards();
        } catch {
            toast({
                title: 'Action failed',
                description: 'Please try again.',
                status: 'error',
                duration: 2500,
            });
        }
    };

    const handleRejectClick = (card: BusinessCard) => {
        setSelectedCard(card);
        setRejectionReason('');
        onOpen();
    };

    const submitRejection = async () => {
        if (!selectedCard) return;
        try {
            await updateCardStatusApi(selectedCard.id, 'reject');
            toast({
                title: 'Card rejected',
                description: 'The employee has been notified to resubmit.',
                status: 'info',
                duration: 2500,
            });
            onClose();
            loadCards();
        } catch {
            toast({
                title: 'Action failed',
                description: 'Please try again.',
                status: 'error',
                duration: 2500,
            });
        }
    };

    const total = cards.length;
    const approved = cards.filter((c) => c.status.toLowerCase() === 'approved' || c.status.toLowerCase() === 'active').length;
    const pending = cards.filter((c) => c.status.toLowerCase() === 'pending').length;

    return (
        <Box bg={pageBg} minH="100vh" fontFamily="'Inter', sans-serif" transition="background 0.25s ease">
            {/* Navbar */}
            <Navbar />

            {/* Page content */}
            <Container maxW="1180px" py={{ base: 6, md: 10 }}>
                <VStack spacing={7} align="stretch">

                    {/* Page header */}
                    <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
                        <Box>
                            <Text
                                fontFamily="'Cormorant Garamond', Georgia, serif"
                                fontSize={{ base: "30px", md: "38px" }}
                                fontWeight={400}
                                color={headingColor}
                                letterSpacing="-0.3px"
                            >
                                Approval Queue
                            </Text>
                            <Text fontSize="13px" color={subtextColor}>
                                Review, verify and approve {company.companyName} corporate business cards
                            </Text>
                        </Box>

                        <Button
                            leftIcon={<AddIcon boxSize="11px" />}
                            bg={company.colors.buttonGradient}
                            color="white"
                            fontSize="13px"
                            fontWeight={600}
                            borderRadius="12px"
                            px={5}
                            h="42px"
                            boxShadow="0 4px 14px rgba(18, 85, 200, 0.25)"
                            _hover={{
                                bg: 'linear-gradient(135deg, #0E398C 0%, #1664EC 100%)',
                                transform: 'translateY(-1px)',
                            }}
                            onClick={() => navigate('/create')}
                        >
                            Create New Card
                        </Button>
                    </Flex>

                    {/* Stat cards */}
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                        <StatCard label="Total Submissions" value={total} accentColor="#1264E8" />
                        <StatCard label="Approved & Live" value={approved} accentColor="#22C55E" />
                        <StatCard label="Pending Review" value={pending} accentColor="#F59E0B" />
                    </Grid>

                    {/* Table card */}
                    <Box
                        bg={tableBg}
                        borderRadius="20px"
                        border="1px solid"
                        borderColor={tableBorder}
                        boxShadow={tableBoxShadow}
                        overflow="hidden"
                    >
                        {/* Table header */}
                        <Flex
                            px={7}
                            py={5}
                            borderBottom="1px solid"
                            borderColor={tableBorder}
                            align="center"
                            justify="space-between"
                        >
                            <HStack spacing={2.5}>
                                <Text fontSize="14px" fontWeight={700} color={tableTitleColor}>
                                    Submissions
                                </Text>
                                <Badge
                                    borderRadius="full"
                                    px={2.5}
                                    py="1px"
                                    bg={badgeBg}
                                    color={badgeColor}
                                    fontSize="11px"
                                    fontWeight={700}
                                >
                                    {total} records
                                </Badge>
                            </HStack>
                        </Flex>

                        <Box overflowX="auto">
                            <Table variant="unstyled" size="sm">
                                <Thead>
                                    <Tr bg={tableHeaderBg} borderBottom="1px solid" borderColor={tableBorder}>
                                        {['Employee', 'Name on Card', 'Department', 'Status', 'Submitted', 'Actions'].map((h) => (
                                            <Th
                                                key={h}
                                                py={3.5}
                                                px={4}
                                                fontSize="11px"
                                                fontWeight={700}
                                                color={thColor}
                                                textTransform="uppercase"
                                                letterSpacing="0.08em"
                                                _first={{ pl: 7 }}
                                                _last={{ pr: 7 }}
                                            >
                                                {h}
                                            </Th>
                                        ))}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {cards.map((card) => (
                                        <Tr
                                            key={card.id}
                                            borderBottom="1px solid"
                                            borderColor={tableBorder}
                                            _hover={{ bg: trHoverBg }}
                                            _last={{ borderBottom: 'none' }}
                                            transition="background 0.15s"
                                        >
                                            {/* Employee */}
                                            <Td py={4} px={4} pl={7}>
                                                <HStack spacing={3}>
                                                    <Center
                                                        w="34px"
                                                        h="34px"
                                                        borderRadius="full"
                                                        bg={tdAvatarBg}
                                                        flexShrink={0}
                                                        overflow="hidden"
                                                        border="1.5px solid"
                                                        borderColor={tdAvatarBorder}
                                                        boxShadow="sm"
                                                    >
                                                        <PassportAvatar card={card} />
                                                    </Center>
                                                    <Box>
                                                        <Text fontSize="13px" fontWeight={600} color={tdTextColor}>
                                                            {card.userName}
                                                        </Text>
                                                        <Text fontSize="11px" color={tdSubtextColor}>
                                                            {card.email}
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                            </Td>

                                            {/* Name on card */}
                                            <Td py={4} px={4}>
                                                <Text
                                                    fontSize="13px"
                                                    fontWeight={600}
                                                    color={tdTextColor}
                                                    textTransform="uppercase"
                                                    letterSpacing="0.04em"
                                                >
                                                    {card.name}
                                                </Text>
                                            </Td>

                                            {/* Department */}
                                            <Td py={4} px={4}>
                                                <Text fontSize="12px" fontWeight={500} color={tdDeptColor}>
                                                    {card.department}
                                                </Text>
                                            </Td>

                                            {/* Status */}
                                            <Td py={4} px={4}>
                                                <StatusBadge status={card.status} />
                                            </Td>

                                            {/* Date */}
                                            <Td py={4} px={4}>
                                                <Text fontSize="12px" color={tdDateColor}>
                                                    {new Date(card.createdAt).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric',
                                                    })}
                                                </Text>
                                            </Td>

                                            {/* Actions */}
                                            <Td py={4} px={4} pr={7}>
                                                <HStack spacing={1.5}>
                                                    <IconButton
                                                        aria-label="View"
                                                        icon={<ViewIcon />}
                                                        size="sm"
                                                        variant="ghost"
                                                        color={tdActionViewColor}
                                                        borderRadius="8px"
                                                        _hover={{ bg: tdActionViewHoverBg, color: 'brand.500' }}
                                                        onClick={() => navigate(`/preview/${(card as any)._id || card.id}`)}
                                                    />
                                                    {card.status.toLowerCase() === 'pending' && (
                                                        <>
                                                            <IconButton
                                                                aria-label="Approve"
                                                                icon={<CheckIcon />}
                                                                size="sm"
                                                                bg="rgba(34, 197, 94, 0.12)"
                                                                color="#22C55E"
                                                                borderRadius="8px"
                                                                _hover={{ bg: 'rgba(34, 197, 94, 0.25)', transform: 'scale(1.05)' }}
                                                                onClick={() => handleApprove(card.id)}
                                                            />
                                                            <IconButton
                                                                aria-label="Reject"
                                                                icon={<CloseIcon boxSize="9px" />}
                                                                size="sm"
                                                                bg="rgba(239, 68, 68, 0.12)"
                                                                color="#EF4444"
                                                                borderRadius="8px"
                                                                _hover={{ bg: 'rgba(239, 68, 68, 0.25)', transform: 'scale(1.05)' }}
                                                                onClick={() => handleRejectClick(card)}
                                                            />
                                                        </>
                                                    )}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>

                        {cards.length === 0 && !loading && (
                            <Center py={16}>
                                <Text fontSize="13px" color={emptyTextColor}>
                                    No submissions in the queue.
                                </Text>
                            </Center>
                        )}
                    </Box>
                </VStack>
            </Container>

            {/* Rejection Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="slideInBottom">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
                <ModalContent
                    borderRadius="20px"
                    mx={4}
                    p={2}
                    bg={modalBg}
                    border="1px solid"
                    borderColor={modalBorder}
                    boxShadow="0 20px 50px rgba(0, 0, 0, 0.4)"
                >
                    <ModalHeader
                        fontFamily="'Cormorant Garamond', Georgia, serif"
                        fontSize="26px"
                        fontWeight={500}
                        color={modalTitleColor}
                        pt={6}
                        pb={1}
                    >
                        Reject Submission
                    </ModalHeader>
                    <Text px={6} fontSize="12px" color={modalSubtextColor} mb={4}>
                        Provide a clear reason so the employee can correct their details.
                    </Text>
                    <ModalCloseButton top={4} right={4} borderRadius="full" size="sm" />
                    <ModalBody>
                        <Textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="e.g. Profile photo resolution is too low, or job title does not match records."
                            rows={4}
                            fontSize="13px"
                            bg={textareaBg}
                            color={textareaColor}
                            borderColor={textareaBorder}
                            borderRadius="12px"
                            _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px #1264E8' }}
                            resize="none"
                        />
                    </ModalBody>
                    <ModalFooter gap={2} pb={6}>
                        <Button
                            variant="outline"
                            borderColor={modalCancelBorder}
                            color={modalCancelColor}
                            fontSize="13px"
                            borderRadius="10px"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            bg="#EF4444"
                            color="white"
                            fontSize="13px"
                            fontWeight={600}
                            borderRadius="10px"
                            _hover={{ bg: '#DC2626' }}
                            isDisabled={!rejectionReason.trim()}
                            onClick={submitRejection}
                        >
                            Confirm Rejection
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </Box>
    );
};

export default SupervisorDashboard;